import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Server, Activity, AlertTriangle, CheckCircle } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { computeEffectiveStatus } from "@/lib/utils";
import { useTranslation } from "@/contexts/TranslationContext";
import { TranslatedText } from "@/components/TranslatedText";

interface ServerStats {
  total: number;
  online: number;
  offline: number;
  warning: number;
  critical: number;
}

export default function DashboardPage() {
  const { profile } = useAuth();
  const { t } = useTranslation();
  const [servers, setServers] = useState<any[]>([]);
  const [stats, setStats] = useState<ServerStats>({
    total: 0,
    online: 0,
    offline: 0,
    warning: 0,
    critical: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (profile?.tenant_id) {
      fetchServers();
    }
  }, [profile]);

  const fetchServers = async () => {
    try {
      const { data: serversData, error } = await supabase
        .from('v_server_status')
        .select('*')
        .eq('tenant_id', profile.tenant_id);

      if (error) throw error;

      setServers(serversData || []);
      
      // Calculate stats using online boolean from view
      const total = serversData?.length || 0;
      const online = serversData?.filter((s: any) => s.online).length || 0;
      const offline = total - online;
      const warning = serversData?.filter((s: any) => s.status === 'warning').length || 0;
      const critical = serversData?.filter((s: any) => s.status === 'critical').length || 0;

      setStats({ total, online, offline, warning, critical });
    } catch (error) {
      console.error('Error fetching servers:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      online: { variant: "default" as const, className: "bg-success text-success-foreground" },
      offline: { variant: "secondary" as const, className: "" },
      warning: { variant: "default" as const, className: "bg-warning text-warning-foreground" },
      critical: { variant: "destructive" as const, className: "" }
    };
    return variants[status as keyof typeof variants] || variants.offline;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online': return <CheckCircle className="h-4 w-4" />;
      case 'warning': return <AlertTriangle className="h-4 w-4" />;
      case 'critical': return <AlertTriangle className="h-4 w-4" />;
      default: return <Server className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold"><TranslatedText text="Dashboard" /></h1>
        <div className="animate-pulse">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <Card key={i}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <div className="h-4 bg-muted rounded w-20"></div>
                  <div className="h-4 w-4 bg-muted rounded"></div>
                </CardHeader>
                <CardContent>
                  <div className="h-8 bg-muted rounded w-16 mb-1"></div>
                  <div className="h-3 bg-muted rounded w-24"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
          <TranslatedText text="Dashboard" />
        </h1>
        <p className="text-muted-foreground">
          <TranslatedText text={`Welkom terug, ${profile?.full_name}. Hier is je server overzicht.`} />
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="relative overflow-hidden group hover:shadow-[var(--shadow-3d-hover)] transition-all duration-300" style={{ boxShadow: 'var(--shadow-3d)' }}>
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 relative z-10">
            <CardTitle className="text-sm font-medium"><TranslatedText text="Total Servers" /></CardTitle>
            <div className="p-2 bg-primary/10 rounded-lg group-hover:scale-110 transition-transform duration-300">
              <Server className="h-4 w-4 text-primary" />
            </div>
          </CardHeader>
          <CardContent className="relative z-10">
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">
              <TranslatedText text="Actieve monitoring targets" />
            </p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden group hover:shadow-[var(--shadow-3d-hover)] transition-all duration-300" style={{ boxShadow: 'var(--shadow-3d)' }}>
          <div className="absolute inset-0 bg-gradient-to-br from-success/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 relative z-10">
            <CardTitle className="text-sm font-medium"><TranslatedText text="Online" /></CardTitle>
            <div className="p-2 bg-success/10 rounded-lg group-hover:scale-110 transition-transform duration-300">
              <CheckCircle className="h-4 w-4 text-success" />
            </div>
          </CardHeader>
          <CardContent className="relative z-10">
            <div className="text-2xl font-bold text-success">{stats.online}</div>
            <p className="text-xs text-muted-foreground">
              <TranslatedText text="Gezonde servers" />
            </p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden group hover:shadow-[var(--shadow-3d-hover)] transition-all duration-300" style={{ boxShadow: 'var(--shadow-3d)' }}>
          <div className="absolute inset-0 bg-gradient-to-br from-warning/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 relative z-10">
            <CardTitle className="text-sm font-medium"><TranslatedText text="Waarschuwingen" /></CardTitle>
            <div className="p-2 bg-warning/10 rounded-lg group-hover:scale-110 transition-transform duration-300">
              <AlertTriangle className="h-4 w-4 text-warning" />
            </div>
          </CardHeader>
          <CardContent className="relative z-10">
            <div className="text-2xl font-bold text-warning">{stats.warning}</div>
            <p className="text-xs text-muted-foreground">
              <TranslatedText text="Vereisen aandacht" />
            </p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden group hover:shadow-[var(--shadow-3d-hover)] transition-all duration-300" style={{ boxShadow: 'var(--shadow-3d)' }}>
          <div className="absolute inset-0 bg-gradient-to-br from-critical/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 relative z-10">
            <CardTitle className="text-sm font-medium"><TranslatedText text="Kritiek" /></CardTitle>
            <div className="p-2 bg-critical/10 rounded-lg group-hover:scale-110 transition-transform duration-300">
              <AlertTriangle className="h-4 w-4 text-critical" />
            </div>
          </CardHeader>
          <CardContent className="relative z-10">
            <div className="text-2xl font-bold text-critical">{stats.critical}</div>
            <p className="text-xs text-muted-foreground">
              <TranslatedText text="Directe actie vereist" />
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="relative overflow-hidden" style={{ boxShadow: 'var(--shadow-3d)' }}>
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-50" />
        <CardHeader className="relative z-10">
          <CardTitle><TranslatedText text="Recente Servers" /></CardTitle>
          <CardDescription>
            <TranslatedText text="Laatst toegevoegde servers aan monitoring" />
          </CardDescription>
        </CardHeader>
        <CardContent className="relative z-10">
          {servers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Server className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2"><TranslatedText text="Nog geen servers" /></p>
              <p><TranslatedText text="Begin met het installeren van monitoring agents op je servers." /></p>
            </div>
          ) : (
            <div className="space-y-4">
              {servers.slice(0, 5).map((server) => (
                <div key={server.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-all duration-200 hover:shadow-md group">
                  <div className="flex items-center space-x-4">
                    <div className="p-2 bg-muted rounded-lg group-hover:bg-primary/10 transition-colors duration-200">
                      {getStatusIcon(server.online ? 'online' : (['warning','critical'].includes(server.status) ? server.status : 'offline'))}
                    </div>
                    <div>
                      <p className="font-medium">{server.hostname}</p>
                      <p className="text-sm text-muted-foreground">{server.ip_address}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <Badge {...getStatusBadge(server.online ? 'online' : (['warning','critical'].includes(server.status) ? server.status : 'offline'))}>
                      {server.online ? 'online' : (['warning','critical'].includes(server.status) ? server.status : 'offline')}
                    </Badge>
                    <div className="text-right text-sm">
                      <p>{(server.cpu_usage !== null && server.cpu_usage !== undefined) ? `CPU: ${server.cpu_usage.toFixed(1)}%` : ''}</p>
                      <p className="text-muted-foreground">
                        {server.last_seen ? new Date(server.last_seen).toLocaleString() : 'Never'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}